import React, { useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import NewGameState from '../../_types/NewGameState';
import {
  FormContainer,
  InputBodyContainer,
  SectionTitle,
} from '../../GameFormComponents';
import CustomSelect from '../../custom-inputs/CustomSelect';
import CustomTextInput from '../../custom-inputs/CustomTextInput';
import { RemoveButton } from '../Rules/Rules.styles';
import featureFlag from '../../../../../common/featureFlag';

type KPIOption = { label: string; value: string; rule: any };

type Props = {
  newGameState: NewGameState;
  onChangeNewGameState: (newGameState: NewGameState) => void;
  kpis: KPIOption[];
};

const hideSetterCloser = featureFlag('noSetterCloser');

const roles = [
  {
    label: 'Anyone',
    value: '', // Persist empty string so backend never receives "anyone"
  },
  ...(hideSetterCloser
    ? []
    : [
        { label: 'Setter', value: 'setter' },
        { label: 'Closer', value: 'closer' },
      ]),
];

const IncentiveView: React.FC<Props> = ({
  newGameState,
  onChangeNewGameState,
  kpis,
}) => {
  const theme = useTheme();

  const rules = Array.isArray(newGameState.rules) ? newGameState.rules : [];
  const kpiSelectItems = useMemo(
    () => kpis.map(({ label, value }) => ({ label, value })),
    [kpis]
  );

  const updateState = useCallback(
    (partial: Partial<NewGameState>) => {
      onChangeNewGameState({ ...newGameState, ...partial });
    },
    [newGameState, onChangeNewGameState]
  );

  useEffect(() => {
    if (newGameState.contestType !== 'incentive') return;

    const rewardCurrency = newGameState.rewardCurrency || 'reward';
    const desiredRewardWhen = 'goal';
    const desiredRewardType = `goal_${rewardCurrency}`;

    if (
      newGameState.rewardWhen !== desiredRewardWhen ||
      newGameState.rewardType !== desiredRewardType
    ) {
      updateState({
        rewardWhen: desiredRewardWhen,
        rewardType: desiredRewardType,
      });
    }
  }, [
    newGameState.contestType,
    newGameState.rewardWhen,
    newGameState.rewardCurrency,
    newGameState.rewardType,
  ]);

  const closeDropdowns = useCallback(() => {
    // No-op retained for compatibility with previous logic
  }, []);

  useEffect(() => {
    // Preserve resize listener for compatibility; no-op currently
    window.addEventListener('resize', closeDropdowns);
    return () => {
      window.removeEventListener('resize', closeDropdowns);
    };
  }, [closeDropdowns]);

  useEffect(() => {
    if (newGameState.contestType !== 'incentive') return;
    if (!Array.isArray(rules) || rules.length === 0) return;

    let hasChange = false;
    const sanitizedRules = rules.map((rule: any) => {
      if (rule?.role === 'anyone') {
        hasChange = true;
        return { ...rule, role: '' };
      }
      return rule;
    });

    if (hasChange) {
      updateState({ rules: sanitizedRules });
    }
  }, [newGameState.contestType, rules, updateState]);

  const addRule = () => {
    const nextRules = [...rules];
    const firstKpi = kpis[0];
    if (!firstKpi) return;
    nextRules.push({
      id: firstKpi.value,
      name: firstKpi.label,
      role: '',
      goal: 1,
      multiplier: 1,
    });
    updateState({ rules: nextRules });
  };

  const removeRule = (ruleIdx: number) => {
    const nextRules = [...rules];
    nextRules.splice(ruleIdx, 1);
    updateState({ rules: nextRules });
  };

  const updateRule = (ruleIdx: number, partial: any) => {
    const nextRules = [...rules];
    nextRules[ruleIdx] = { ...nextRules[ruleIdx], ...partial };
    updateState({ rules: nextRules });
  };

  return (
    <FormContainer>
      <SectionTitle>Incentive KPI Rules</SectionTitle>
      <InputBodyContainer>
        {(rules || []).map((rule: any, ruleIdx: number) => (
          <div
            key={`rule-${ruleIdx}`}
            style={{
              border: '1px solid #D0D2D8',
              borderRadius: 8,
              padding: 12,
              marginTop: 12,
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'flex-end',
              }}
            >
              <div style={{ flex: 1 }}>
                <CustomSelect
                  label="KPI"
                  value={rule.id || ''}
                  onChange={(val: any) => {
                    const k = kpis.find((x) => x.value === val);
                    if (!k) return;
                    updateRule(ruleIdx, { id: val, name: k.label });
                  }}
                  items={kpiSelectItems}
                />
              </div>
              <div style={{ width: 110 }}>
                <CustomSelect
                  label="Role (optional)"
                  value={rule.role || ''}
                  onChange={(val: any) => updateRule(ruleIdx, { role: val })}
                  items={roles}
                  placeholder="Select"
                  allowEmptyValue
                />
              </div>
              <div style={{ width: 70 }}>
                <CustomTextInput
                  label="Goal*"
                  value={String(rule.goal ?? 1)}
                  onChange={(text: string) => {
                    const goal = text === '' ? 0 : Number(text);
                    updateRule(ruleIdx, { goal });
                  }}
                />
              </div>
              <div style={{ width: 70 }}>
                <CustomTextInput
                  label="Multiplier"
                  value={String(rule.multiplier ?? 1)}
                  onChange={(text: string) => {
                    const multiplier = text === '' ? 1 : Number(text);
                    updateRule(ruleIdx, { multiplier });
                  }}
                />
              </div>
              <div>
                <RemoveButton onClick={() => removeRule(ruleIdx)}>
                  Remove
                </RemoveButton>
              </div>
            </div>
          </div>
        ))}

        <div style={{ marginTop: 12 }}>
          <div
            onClick={addRule}
            style={{
              color: (theme as any)?.custom?.primary || '#FF6A00',
              padding: '8px 16px',
              borderRadius: 4,
              fontSize: 14,
              fontWeight: 'bold',
              cursor: kpis.length ? 'pointer' : 'not-allowed',
              display: 'inline-flex',
              alignItems: 'center',
              opacity: kpis.length ? 1 : 0.5,
              pointerEvents: kpis.length ? 'auto' : 'none',
            }}
          >
            + Add KPI Rule
          </div>
        </div>
      </InputBodyContainer>
    </FormContainer>
  );
};

export default IncentiveView;
